import { Timestamp } from 'firebase/firestore';

export type SubscriptionPlanId =
  | 'trial'
  | 'solo'
  | 'pro'
  | 'center'
  | 'enterprise';
export type LegacySubscriptionPlanId = 'free' | 'basic' | 'premium';
export type BillingCycle = 'monthly' | 'yearly';
export type SubscriptionStatus =
  | 'active'
  | 'cancelled'
  | 'expired'
  | 'trial'
  | 'read_only';

export interface PublicPricingPlan {
  id: SubscriptionPlanId;
  nameAr: string;
  audienceAr: string;
  monthlyPriceEgp: number;
  yearlyPriceEgp: number | null;
  isPublic?: boolean;
  includedActiveStudents: number;
  activeStudentOverageEgp: number | null;
  maximumActiveStudents: number;
  maxEmployees: number;
  students: {
    included: number;
    blockSize: number;
    blockPriceEgp: number;
    recommendedMaximum?: number;
  };
  storage: {
    includedGb: number;
    blockSizeGb: number;
    blockPriceEgp: number;
  };
  bandwidth: {
    includedGb: number;
    blockSizeGb: number;
    blockPriceEgp: number;
    reliableMetering: boolean;
  };
  limits: {
    employees: number;
  };
  features: {
    parents: boolean;
    zoomIntegration: boolean;
    paymentIntegration: boolean;
    accounting: 'basic' | 'advanced';
    reports: 'basic' | 'advanced';
    excelExport: boolean;
    employeeManagement: boolean;
    permissionManagement: boolean;
    assignments: boolean;
    advancedAssignmentReports: boolean;
    assignmentExcelExport: boolean;
    assignmentsManagedByEmployees: boolean;
    assignmentSubmissionAnalytics: boolean;
    lateSubmissionAnalytics: boolean;
    assignmentPerformanceComparison: boolean;
  };
  trialDays?: number;
  isContactOnly?: boolean;
  isPopular?: boolean;
}

export interface PublicPricingCatalog {
  version: string;
  currency: 'EGP';
  annualSavingsMonths: number;
  plans: PublicPricingPlan[];
  thirdPartyCostsAr: string[];
}

export interface AnnualSavingsSnapshot {
  monthlyAnnualEquivalentEgp: number;
  annualSavingsEgp: number;
  annualSavingsPercent: number;
  annualSavingsMonths: number;
}

export interface SubscriptionPlan {
  id: SubscriptionPlanId;
  nameAr: string;
  nameEn: string;
  featuresAr: string[];
  limits: {
    maxStudents: number;
    maxClasses: number;
    maxEmployees: number;
    maxSessionsPerMonth: number;
    canUseZoom: boolean;
    canUseCoupons: boolean;
    canManageEmployees: boolean;
    canExportReports: boolean;
    hasPrioritySupport: boolean;
    storageGB: number;
  };
}

export interface SubscriptionPricingSnapshot {
  pricingVersion: string;
  basePriceEgp: number;
  monthlyPriceEgp?: number;
  yearlyPriceEgp?: number | null;
  includedActiveStudents: number;
  activeStudentOverageEgp: number | null;
  maximumActiveStudents: number;
  students?: PublicPricingPlan['students'];
  storage?: PublicPricingPlan['storage'];
  bandwidth?: PublicPricingPlan['bandwidth'];
  features?: PublicPricingPlan['features'];
  limits?: PublicPricingPlan['limits'];
}

export interface TeacherSubscription {
  teacherId: string;
  planId: SubscriptionPlanId;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;
  currentPeriodStart: Timestamp;
  currentPeriodEnd: Timestamp;
  trialEndDate?: Timestamp;
  cancelledAt?: Timestamp;
  pricingSnapshot?: SubscriptionPricingSnapshot;
  usageThisMonth: {
    sessions: number;
    activeStudents?: number;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export const PLAN_ENTITLEMENTS: Record<SubscriptionPlanId, SubscriptionPlan> = {
  trial: {
    id: 'trial',
    nameAr: 'تجربة Pro',
    nameEn: 'Pro Trial',
    featuresAr: ['جميع مميزات Pro لمدة 14 يومًا', 'حتى 50 طالبًا نشطًا'],
    limits: {
      maxStudents: 50,
      maxClasses: -1,
      maxEmployees: 0,
      maxSessionsPerMonth: -1,
        canUseZoom: true,
        canUseCoupons: true,
        canManageEmployees: false,
        canExportReports: true,
        hasPrioritySupport: false,
        storageGB: 5,
    },
  },
  solo: {
    id: 'solo',
    nameAr: 'المدرس Solo',
    nameEn: 'Solo Teacher',
    featuresAr: [
      'إدارة الطلاب والحضور والحصص',
      'الواجبات والمدفوعات',
      'تقارير أساسية',
      'بوابات الطالب وولي الأمر',
    ],
    limits: {
      maxStudents: 100,
      maxClasses: -1,
      maxEmployees: 0,
      maxSessionsPerMonth: -1,
      canUseZoom: true,
      canUseCoupons: true,
      canManageEmployees: false,
      canExportReports: false,
      hasPrioritySupport: false,
      storageGB: 5,
    },
  },
  pro: {
    id: 'pro',
    nameAr: 'المدرس Pro',
    nameEn: 'Pro Teacher',
    featuresAr: [
      'تكامل Zoom والكوبونات',
      'التصدير والتقارير المتقدمة',
      'حتى 5 موظفين',
      'دعم فني أسرع',
    ],
    limits: {
      maxStudents: 500,
      maxClasses: -1,
      maxEmployees: 5,
      maxSessionsPerMonth: -1,
      canUseZoom: true,
      canUseCoupons: true,
      canManageEmployees: true,
      canExportReports: true,
      hasPrioritySupport: true,
      storageGB: 20,
    },
  },
  center: {
    id: 'center',
    nameAr: 'السنتر Center',
    nameEn: 'Center',
    featuresAr: [
      'إدارة متكاملة للسنتر',
      'الموارد البشرية والصلاحيات الموسعة',
      'حتى 10 موظفين',
      'تقارير ودعم متقدم',
    ],
    limits: {
      maxStudents: 1500,
      maxClasses: -1,
      maxEmployees: 10,
      maxSessionsPerMonth: -1,
      canUseZoom: true,
      canUseCoupons: true,
      canManageEmployees: true,
      canExportReports: true,
      hasPrioritySupport: true,
      storageGB: 50,
    },
  },
  enterprise: {
    id: 'enterprise',
    nameAr: 'المؤسسات',
    nameEn: 'Enterprise',
    featuresAr: [
      'عقد مخصص حسب الخصائص الجاهزة',
      'تهيئة ودعم وفق نطاق متفق عليه',
    ],
    limits: {
      maxStudents: -1,
      maxClasses: -1,
      maxEmployees: -1,
      maxSessionsPerMonth: -1,
      canUseZoom: true,
      canUseCoupons: true,
      canManageEmployees: true,
      canExportReports: true,
      hasPrioritySupport: true,
      storageGB: -1,
    },
  },
};

export const normalizeSubscriptionPlanId = (
  value: unknown
): SubscriptionPlanId => {
  if (value === 'free') return 'trial';
  if (value === 'basic') return 'solo';
  if (value === 'premium') return 'pro';
  return value === 'trial' ||
    value === 'solo' ||
    value === 'pro' ||
    value === 'center' ||
    value === 'enterprise'
    ? value
    : 'trial';
};

export const normalizeTeacherSubscription = (
  subscription: Omit<TeacherSubscription, 'planId'> & {
    planId: SubscriptionPlanId | LegacySubscriptionPlanId;
  }
): TeacherSubscription => ({
  ...subscription,
  planId: normalizeSubscriptionPlanId(subscription.planId),
});

type PlanFeatureMap = PublicPricingPlan['features'];
type PlanLimitMap = PublicPricingPlan['limits'];

const PLAN_FEATURES: Record<SubscriptionPlanId, PlanFeatureMap> = {
  trial: {
    parents: true,
    zoomIntegration: true,
    paymentIntegration: true,
    accounting: 'advanced',
    reports: 'advanced',
    excelExport: true,
    employeeManagement: false,
    permissionManagement: false,
    assignments: true,
    advancedAssignmentReports: true,
    assignmentExcelExport: true,
    assignmentsManagedByEmployees: false,
    assignmentSubmissionAnalytics: true,
    lateSubmissionAnalytics: true,
    assignmentPerformanceComparison: true,
  },
  solo: {
    parents: true,
    zoomIntegration: true,
    paymentIntegration: true,
    accounting: 'basic',
    reports: 'basic',
    excelExport: false,
    employeeManagement: false,
    permissionManagement: false,
    assignments: true,
    advancedAssignmentReports: false,
    assignmentExcelExport: false,
    assignmentsManagedByEmployees: false,
    assignmentSubmissionAnalytics: false,
    lateSubmissionAnalytics: false,
    assignmentPerformanceComparison: false,
  },
  pro: {
    parents: true,
    zoomIntegration: true,
    paymentIntegration: true,
    accounting: 'advanced',
    reports: 'advanced',
    excelExport: true,
    employeeManagement: true,
    permissionManagement: true,
    assignments: true,
    advancedAssignmentReports: true,
    assignmentExcelExport: true,
    assignmentsManagedByEmployees: true,
    assignmentSubmissionAnalytics: true,
    lateSubmissionAnalytics: true,
    assignmentPerformanceComparison: true,
  },
  center: {
    parents: true,
    zoomIntegration: true,
    paymentIntegration: true,
    accounting: 'advanced',
    reports: 'advanced',
    excelExport: true,
    employeeManagement: true,
    permissionManagement: true,
    assignments: true,
    advancedAssignmentReports: true,
    assignmentExcelExport: true,
    assignmentsManagedByEmployees: true,
    assignmentSubmissionAnalytics: true,
    lateSubmissionAnalytics: true,
    assignmentPerformanceComparison: true,
  },
  enterprise: {
    parents: true,
    zoomIntegration: true,
    paymentIntegration: true,
    accounting: 'advanced',
    reports: 'advanced',
    excelExport: true,
    employeeManagement: true,
    permissionManagement: true,
    assignments: true,
    advancedAssignmentReports: true,
    assignmentExcelExport: true,
    assignmentsManagedByEmployees: true,
    assignmentSubmissionAnalytics: true,
    lateSubmissionAnalytics: true,
    assignmentPerformanceComparison: true,
  },
};

export const hasFeature = (
  planId: unknown,
  feature: keyof PlanFeatureMap
): boolean => Boolean(PLAN_FEATURES[normalizeSubscriptionPlanId(planId)][feature]);

export const getPlanLimit = (
  planId: unknown,
  limit: keyof PlanLimitMap
): number => {
  const plan = PLAN_ENTITLEMENTS[normalizeSubscriptionPlanId(planId)];
  if (limit === 'employees') return plan.limits.maxEmployees;
  return -1;
};
